<template>
  <modal v-model="dialogModel" max-width="600">
    <template #title>Publish Draft</template>
    <template #content>
      <div
        v-for="category in categories"
        :key="category.label"
        class="mb-6"
      >
        <div class="d-flex mb-4">
          <div class="etpd-icon mr-3" :class="category.iconClass">
            <v-icon x-small color="white">
              {{ category.icon }}
            </v-icon>
          </div>
          <span class="etpd-subtitle">{{ category.label }}</span>
        </div>
        <div
          v-for="(field, fieldIdx) in category.fields"
          :key="fieldIdx"
          class="d-flex mb-3"
        >
          <v-icon class="mr-6"> format_list_bulleted </v-icon>
          {{ field }}
        </div>
      </div>
      <div class="etpd-warning mt-10">
        <v-icon color="warning lighten-2"> warning </v-icon>
        <div>
          If you choose to publish these changes, you will need to
          update and re-assess any data extraction reports that have
          been previously reviewed for consensus.
        </div>
      </div>
    </template>
    <template #actions>
      <action-button class="mr-3" @click="dialogModel = false">
        Cancel
      </action-button>
      <action-button
        class="mr-4"
        color="primary"
        :disabled="disabled || !open"
        @click="$emit('publish')"
      >
        <v-icon left class="material-icons-outlined">publish</v-icon>
        Publish Draft
      </action-button>
    </template>
  </modal>
</template>

<script>
import ActionButton from '@/components/Common/ActionButton';
import Modal from '@/components/Common/Modal';
import { DataExtractionFieldDraft } from '@/models';
import { deepEqualExcludingId } from '@/modules/compareContents';
import { isArrayOfType } from '@/modules/typeHelpers';

export default {
  name: 'ExtractionTemplatePublishDialog',
  components: {
    ActionButton,
    Modal,
  },
  props: {
    open: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    fieldDrafts: {
      type: Array,
      required: true,
      validator: (val) =>
        isArrayOfType(val, DataExtractionFieldDraft),
    },
  },
  emits: ['update:open', 'publish'],
  computed: {
    dialogModel: {
      get: function () {
        return this.open;
      },
      set: function (value) {
        this.$emit('update:open', value);
      },
    },
    addedFields() {
      return this.fieldDrafts
        .filter(({ existingField, updatedField }) => {
          return updatedField && !existingField;
        })
        .map(({ updatedField }) => updatedField.title);
    },
    modifiedFields() {
      return this.fieldDrafts
        .filter(({ existingField, updatedField }) => {
          if (!existingField || !updatedField) {
            return false;
          }
          return !deepEqualExcludingId(existingField, updatedField);
        })
        .map(({ updatedField }) => updatedField.title);
    },
    deletedFields() {
      return this.fieldDrafts
        .filter(({ existingField, updatedField, modified }) => {
          return existingField && modified && !updatedField;
        })
        .map(({ existingField }) => existingField.title);
    },
    categories() {
      const { addedFields, modifiedFields, deletedFields } = this;
      const ret = [];
      if (addedFields.length > 0) {
        ret.push({
          label: 'Added',
          iconClass: 'added',
          icon: 'add',
          fields: addedFields,
        });
      }
      if (modifiedFields.length > 0) {
        ret.push({
          label: 'Modified',
          iconClass: 'modified',
          icon: 'edit',
          fields: modifiedFields,
        });
      }
      if (deletedFields.length > 0) {
        ret.push({
          label: 'Removed',
          iconClass: 'removed',
          icon: 'remove',
          fields: deletedFields,
        });
      }
      return ret;
    },
  },
};
</script>

<style lang="scss" scoped>
.etpd-subtitle {
  font-weight: 600;
  font-size: 16px;
}

.etpd-icon {
  display: grid;
  width: 18px;
  height: 18px;
  color: white;
  border-radius: 20px;
  place-content: center;

  &.added {
    background-color: var(--v-primary-base);
  }

  &.modified {
    background-color: var(--v-success-base);
  }

  &.removed {
    background-color: var(--v-error-base);
  }
}

.etpd-warning {
  display: grid;
  grid-template-columns: 48px 1fr;
  padding: 1rem;
  place-items: start;
  background-color: var(--v-warning-lighten5);
  border: 1px solid var(--v-warning-lighten2);
}
</style>
